import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../redux/OrderRedux';
import { listSaleableProducts } from '../services/OrderCRUD';
import { currentUserSelector } from '../../user-profile';
import { Line } from '../models/Line';
import { listStores } from '../../plans';

export default function ReplaceProductModal({
  visible,
  onClose,
  line,
}: {
  visible: boolean;
  onClose: () => void;
  line: Line | null;
}) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState(null);
  const [units, setUnits] = useState(1);
  const intl = useIntl();
  const dispatch = useDispatch();
  const currentUser = useSelector(currentUserSelector);

  async function fetchProducts() {
    try {
      const customerCountry = currentUser?.store.country.code.toUpperCase();
      const { data: stores } = await listStores();
      const store = stores.find((s) => s.country === customerCountry);
      if (!store) return;
      const { data } = await listSaleableProducts(store.id);

      setProducts(
        data
          // Only dry products can replace a dry line, and never the line itself.
          .filter(
            (product) =>
              product.kind === 'dry' && product.id !== line?.product?.id
          )
          .map((product) => ({
            value: product.id,
            label: `${product.name} - ${product?.price || '-'}€`,
            disabled: !product?.price,
          }))
          .sort((a, b) => a.label.localeCompare(b.label))
      );
    } catch (error) {
      console.warn(error);
    }
  }

  useEffect(() => {
    setSelectedProduct(null);
    fetchProducts();
  }, [currentUser?.store.country.code]);

  useEffect(() => {
    setSelectedProduct(null);
    setUnits(line?.units || 1);
  }, [visible, line]);

  function onConfirm() {
    dispatch(
      actions.replaceProductInSelectedOrder({
        oldProductId: line.product.id,
        newProductId: parseInt(selectedProduct.value, 10),
        units,
      })
    );
    onClose();
  }

  return (
    <Modal show={visible} onClose={onClose}>
      <Modal.Header>
        <h4>
          <FormattedMessage
            id="orderDetail.replaceDryLine"
            values={{
              name: line?.product?.product?.name || line?.name || '',
            }}
          />
        </h4>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group mb-6">
          <label className="fw-bolder mb-2">
            <FormattedMessage id="orderDetail.selectReplacementProduct" />
          </label>
          <Select
            className="react-select-container"
            classNamePrefix="react-select"
            options={products}
            value={selectedProduct}
            isOptionDisabled={(option) => option.disabled}
            onChange={setSelectedProduct}
            placeholder={intl.formatMessage({ id: 'orderDetail.select' })}
          />
        </div>
        {selectedProduct && (
          <div className="form-group w-75">
            <label className="fw-bolder mb-2">
              <FormattedMessage id="orderDetail.units" />
            </label>
            <input
              min={1}
              max={1000}
              type="number"
              className="form-control"
              value={units}
              onChange={(e) => setUnits(parseInt(e.target.value, 10))}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <Button variant="tertiary" onClick={onClose}>
            <FormattedMessage id="orderDetail.close" />
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={!(selectedProduct && units > 0)}
          >
            <FormattedMessage id="orderDetail.replaceProduct" />
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
